import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "../../query";
import type {
  AccessToken,
  Features,
  Model,
  PlaygroundPermissions,
  Prediction,
  Version,
} from "../../types";
import { PlaygroundContext, type PlaygroundContextValue } from "./context";
import PredictionOutput from "./prediction-output";
import { getRenderMode } from "./util";

export default function PredictionReportOutput({
  prediction,
  model,
  version,
  features,
  token,
  permissions,
  isAuthenticated,
}: {
  prediction: Prediction | null | undefined;
  model: Model;
  version: Version;
  features: Features;
  token?: AccessToken | null;
  permissions: PlaygroundPermissions;
  isAuthenticated: boolean;
}) {
  if (!prediction) {
    Sentry.captureEvent({
      message: "Somehow reporting a prediction's output without a prediction.",
      level: "error",
    });
    return null;
  }

  const context: PlaygroundContextValue = {
    elementVisibility: {
      tweakButton: false,
    },
    features,
    hideAdvancedInputs: false,
    hideVersionMismatchWarning: false,
    isAuthenticated,
    modelStatus: null,
    permissions,
    renderMode: getRenderMode({ features }),
    token: token ?? null,
    version: version,
  };

  return (
    <QueryClientProvider client={queryClient}>
      <PlaygroundContext.Provider value={context}>
        <PredictionOutput
          alwaysRenderURLsAsDownload={false}
          displayAsJson={false}
          prediction={prediction}
          reportFallback
          shouldAutoScroll={false}
        />
      </PlaygroundContext.Provider>
    </QueryClientProvider>
  );
}
